import { useEffect, useState } from "react";
import {
  ActionIcon,
  Box,
  Flex,
  List,
  Text,
  useMantineTheme,
} from "@mantine/core";
import TextInput from "src/components/TextInput/TextInput";
import SegmentedControl from "src/components/SegmentedControl";
import { BiSearch } from "react-icons/bi";
import { ModalStages } from "src/utils/constants";
import { MdClose } from "react-icons/md";
import {
  GetAgreementManyQuery,
  GetClientGroupManyQuery,
  GetSignedAgreementsQuery,
} from "src/generated/graphql";

export enum FormTypes {
  NewAgreement = "New Agreement",
  GroupAgreement = "Apply Group Agreement",
}
interface AgreementListProps {
  clientGroups: GetClientGroupManyQuery["clientGroupMany"] | [];
  agreementsList: GetAgreementManyQuery["agreementMany"] | [];
  existingAgreements: GetSignedAgreementsQuery["signedAgreementMany"];
  setSelectedAgreementId: (e: string) => void;
  setCurrentModalStage: (e: ModalStages) => void;
  setIsNewAgreement: (e: boolean) => void;
  handleModalClose: () => void;
}

const AgreementsListModal = ({
  agreementsList,
  existingAgreements,
  clientGroups,
  setSelectedAgreementId,
  setCurrentModalStage,
  setIsNewAgreement,
  handleModalClose,
}: AgreementListProps) => {
  const theme = useMantineTheme();
  const [formType, setFormType] = useState<FormTypes>(FormTypes.NewAgreement);
  const [searchedValue, setSearchedValue] = useState("");
  const [searchedList, setSearchedList] =
    useState<AgreementListProps["agreementsList"]>(agreementsList);

  const handleFormChange = (e: FormTypes) => {
    setFormType(e);
    if (e === FormTypes.NewAgreement) {
      setIsNewAgreement(true);
    } else {
      setIsNewAgreement(false);
    }
  };

  const handleModals = (id: string, isGroup: boolean) => {
    setSelectedAgreementId(id);
    if (isGroup) {
      setCurrentModalStage(ModalStages.AGREEMENTGROUPS);
    } else {
      setCurrentModalStage(ModalStages.AGREEMENTOVERVIEW);
    }
  };

  useEffect(() => {
    let filteredAgreements = [...agreementsList];
    if (existingAgreements) {
      filteredAgreements = filteredAgreements.filter(
        (e) => !existingAgreements.find((el) => el.agreement.id === e.id)
      );
    }

    if (searchedValue.trim() === "") {
      setSearchedList(filteredAgreements);
    } else {
      filteredAgreements = filteredAgreements.filter((e) =>
        e.name.toLowerCase().includes(searchedValue.toLowerCase())
      );
      setSearchedList(filteredAgreements);
    }
  }, [searchedValue, agreementsList]);

  return (
    <div
      style={{
        width: "100%",
        minHeight: 500,
        display: "flex",
        flexDirection: "column",
        borderRadius: 4,
      }}
    >
      <Flex
        align={"center"}
        justify={"space-between"}
        style={{
          padding: "10px 20px",
          borderBottom: `2px solid ${theme.colors.light[5]}`,
        }}
      >
        <Text size={22} fw={500}>
          Agreement Flow Section
        </Text>
        <ActionIcon size="lg" onClick={handleModalClose}>
          <MdClose size={20} />
        </ActionIcon>
      </Flex>
      <form
        style={{
          backgroundColor: theme.colors.secondary[6],
          borderRadius: 4,
          flex: 1,
          padding: "10px 20px",
        }}
      >
        <Flex
          //my={10}
          py={10}
          align={"center"}
          justify={"center"}
          style={{ borderBottom: `1px solid ${theme.colors.light[3]}` }}
        >
          <SegmentedControl
            data={[FormTypes.NewAgreement, FormTypes.GroupAgreement]}
            value={formType}
            onChange={handleFormChange}
          />
        </Flex>

        {formType === FormTypes.GroupAgreement ? (
          <>
            <Flex direction={"column"} m={10}>
              {clientGroups?.map((group) => {
                if (
                  group?.signedAgreement &&
                  group?.signedAgreement?.agreement?.id
                ) {
                  const agreementId = group?.signedAgreement?.agreement?.id;
                  return (
                    <Flex
                      key={group.id}
                      align={"center"}
                      justify={"space-between"}
                      py={10}
                      style={{
                        borderBottom: `1px solid ${theme.colors.secondary[2]}`,
                      }}
                      onClick={() => {
                        handleModals(agreementId, false);
                      }}
                    >
                      <Flex direction={"column"}>
                        <Text size={14} fw={400}>
                          {group.signedAgreement.agreement.name}
                        </Text>
                        <Text size={14} fw={400}>
                          {group.signedAgreement.agreement.description}
                        </Text>
                      </Flex>
                      <Text size={14} fw={400}>
                        {group.name}
                      </Text>
                    </Flex>
                  );
                }
              })}
            </Flex>
          </>
        ) : (
          <>
            <TextInput
              placeholder="Search Agreement"
              icon={<BiSearch size="0.8rem" />}
              value={searchedValue}
              onChange={(e) => setSearchedValue(e.target.value)}
              style={{
                borderRadius: "25px",
              }}
            />
            <List
              mt={15}
              styles={{
                itemWrapper: {
                  width: "100%",
                },
                item: {
                  listStyle: "none",
                  color: theme.colors.secondaryTextColor[6],
                  borderBottom: `1px solid ${theme.colors.secondary[0]}`,
                  fontSize: 13,
                  cursor: "pointer",
                  ":hover": {
                    color: theme.colors.primaryTextColor[6],
                    background: theme.colors.primary[6],
                  },
                },
              }}
            >
              {searchedList?.map((item: any) => {
                let totalCost = 0;
                item?.recurringCharges?.map(
                  (e: any) => (totalCost = totalCost + e.cost)
                );
                return (
                  <List.Item
                    py={15}
                    key={item.id}
                    onClick={() => {
                      handleModals(item.id, item?.isGroup);
                    }}
                  >
                    <Flex
                      w={"100%"}
                      px={10}
                      align={"center"}
                      justify={"space-between"}
                    >
                      <Flex w={"85%"}>
                        {/* <Box w={50} h={50} bg={theme.colors.light[2]}></Box> */}
                        <Flex direction={"column"} px={5} w={"100%"}>
                          <Text size={14} fw={500}>
                            {item.name}
                          </Text>
                          <Text>{item.description}</Text>
                        </Flex>
                      </Flex>
                      <Flex>
                        <Text px={5} size={16} fw={500}>{`$${totalCost}`}</Text>
                      </Flex>
                    </Flex>
                  </List.Item>
                );
              })}
            </List>
          </>
        )}
      </form>
    </div>
  );
};

export default AgreementsListModal;
